/** Estimate a complete subject on a quiet studio background; never edit image pixels. */
export function measureCatalogSubject({ data, width: w, height: h }) {
  const full = { box: [0, 0, 1, 1], target: 1, confidence: 'complete-photo' };
  if (w < 32 || h < 32) return full;
  const edge = Math.max(3, Math.round(w * .045));
  let transparentEdge = 0;
  for (let x = 0; x < w; x++) {
    if (data[x * 4 + 3] < 32) transparentEdge++;
    if (data[((h - 1) * w + x) * 4 + 3] < 32) transparentEdge++;
  }
  const alphaBackground = transparentEdge > w * .5;
  const median = values => values.sort((a, b) => a - b)[values.length >> 1];
  const sides = [[], []];
  for (let y = 0; y < h; y++) {
    for (let side = 0; side < 2; side++) {
      const rgb = [[], [], []];
      for (let x = 1; x <= edge; x++) {
        const p = (y * w + (side ? w - 1 - x : x)) * 4;
        for (let c = 0; c < 3; c++) rgb[c].push(data[p + c]);
      }
      sides[side].push(rgb.map(median));
    }
  }
  const mask = new Uint8Array(w * h);
  let edgeHits = 0, edgePixels = 0, hits = 0;
  for (let y = 0; y < h; y++) {
    const background = sides.map(rows => {
      const rgb = [[], [], []];
      for (let yy = Math.max(0, y - 4); yy <= Math.min(h - 1, y + 4); yy++) {
        for (let c = 0; c < 3; c++) rgb[c].push(rows[yy][c]);
      }
      return rgb.map(median);
    });
    for (let x = 0; x < w; x++) {
      const p = (y * w + x) * 4, t = x / (w - 1);
      let distance = 0;
      for (let c = 0; c < 3; c++) {
        const expected = background[0][c] * (1 - t) + background[1][c] * t;
        distance += (data[p + c] - expected) ** 2;
      }
      const foreground = alphaBackground ? data[p + 3] > 32 : distance > 3 * 22 ** 2;
      mask[y * w + x] = foreground ? 1 : 0;
      if (foreground) hits++;
      if (x < edge || x >= w - edge || y < 3 || y >= h - 3) {
        edgePixels++; if (foreground) edgeHits++;
      }
    }
  }
  if (edgeHits / edgePixels > .22 || hits / (w * h) > .73 || hits / (w * h) < .025) return full;
  const components = [], queue = new Int32Array(w * h);
  for (let p = 0; p < mask.length; p++) {
    if (!mask[p]) continue;
    let head = 0, tail = 1, minX = w, minY = h, maxX = 0, maxY = 0;
    queue[0] = p; mask[p] = 0;
    while (head < tail) {
      const at = queue[head++], x = at % w, y = Math.floor(at / w);
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      for (let yy = Math.max(0, y - 1); yy <= Math.min(h - 1, y + 1); yy++) {
        for (let xx = Math.max(0, x - 1); xx <= Math.min(w - 1, x + 1); xx++) {
          const next = yy * w + xx;
          if (mask[next]) { mask[next] = 0; queue[tail++] = next; }
        }
      }
    }
    components.push({ pixels: tail, box: [minX, minY, maxX + 1, maxY + 1] });
  }
  components.sort((a, b) => b.pixels - a.pixels);
  if (!components[0] || components[0].pixels < w * h * .015) return full;
  const relevant = components.filter(c => c.pixels >= Math.max(8, components[0].pixels * .009));
  const bounds = [w, h, 0, 0];
  for (const { box } of relevant) {
    bounds[0] = Math.min(bounds[0], box[0]); bounds[1] = Math.min(bounds[1], box[1]);
    bounds[2] = Math.max(bounds[2], box[2]); bounds[3] = Math.max(bounds[3], box[3]);
  }
  // A molding running from top to bottom belongs to the setting, not the piece.
  if (bounds[1] <= h * .01 && bounds[3] >= h * .99) return full;
  const pad = Math.max(3, Math.round(Math.max(w, h) * .025));
  const box = [Math.max(0, bounds[0] - pad) / w, Math.max(0, bounds[1] - pad) / h,
    Math.min(w, bounds[2] + pad) / w, Math.min(h, bounds[3] + pad) / h];
  if ((box[2] - box[0]) * (box[3] - box[1]) > .94) return full;
  const ratio = (box[2] - box[0]) * w / ((box[3] - box[1]) * h);
  return { box, target: ratio < .58 ? .90 : ratio > 1.15 ? .87 : .88, confidence: 'studio' };
}

if (typeof window !== 'undefined' && !customElements.get('tiffany-catalog-photo')) {
  const measurements = new Map();
  const version = 'tm-photo-v3:';
  async function measureImage(image) {
    const url = new URL(image.currentSrc || image.src, location.href);
    url.searchParams.delete('width'); url.searchParams.delete('height');
    const key = version + url.href;
    if (measurements.has(key)) return measurements.get(key);
    const task = (async () => {
      try { const cached = JSON.parse(sessionStorage.getItem(key)); if (cached?.box?.length === 4) return cached; } catch {}
      const sample = new Image(); sample.crossOrigin = 'anonymous';
      url.searchParams.set('width', '256'); sample.src = url.href;
      await sample.decode();
      const scale = 220 / Math.max(sample.naturalWidth, sample.naturalHeight);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(32, Math.round(sample.naturalWidth * scale));
      canvas.height = Math.max(32, Math.round(sample.naturalHeight * scale));
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(sample, 0, 0, canvas.width, canvas.height);
      const result = measureCatalogSubject(context.getImageData(0, 0, canvas.width, canvas.height));
      try { sessionStorage.setItem(key, JSON.stringify(result)); } catch {}
      return result;
    })().catch(() => ({ box: [0, 0, 1, 1], target: 1, confidence: 'complete-photo' }));
    measurements.set(key, task); return task;
  }

  class TiffanyCatalogPhoto extends HTMLElement {
    #image; #measurement; #frame = 0; #source = ''; #request = 0; #pending = false;
    #resize = new ResizeObserver(() => this.#schedule());
    #changes = new MutationObserver(() => {
      const image = this.querySelector('img');
      if (image && image !== this.#image) {
        this.#image?.removeEventListener('load', this.#prepare);
        this.#image = image; image.addEventListener('load', this.#prepare);
        this.#changes.observe(image, { attributes: true, attributeFilter: ['src', 'srcset'] });
      }
      this.#prepare(); this.#schedule();
    });
    connectedCallback() {
      this.#image = this.querySelector('img'); if (!this.#image) return;
      this.#image.addEventListener('load', this.#prepare);
      this.#resize.observe(this);
      this.#changes.observe(this.#image, { attributes: true, attributeFilter: ['src', 'srcset'] });
      this.#changes.observe(this, { childList: true });
      const slideshow = this.closest('slideshow-component');
      if (slideshow) this.#changes.observe(slideshow, { subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'hidden'] });
      this.#prepare(); this.#schedule();
    }
    disconnectedCallback() {
      this.#request++; this.#pending = false; this.#source = ''; this.#image?.removeEventListener('load', this.#prepare);
      this.#resize.disconnect(); this.#changes.disconnect(); cancelAnimationFrame(this.#frame); this.#frame = 0;
    }
    #prepare = async () => {
      if (!this.#image) return;
      const source = this.#image.src;
      if (source !== this.#source) {
        this.#source = source; this.#measurement = undefined; this.#pending = false; this.#request++;
        this.removeAttribute('data-framed'); delete this.dataset.framing;
      }
      if (!this.#image.complete || !this.#image.naturalWidth || this.#measurement || this.#pending) return;
      this.#pending = true;
      const request = ++this.#request;
      const measurement = await measureImage(this.#image);
      if (!this.isConnected || request !== this.#request) return;
      this.#pending = false; this.#measurement = measurement; this.dataset.framing = measurement.confidence; this.#schedule();
    };
    #schedule() {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => { this.#frame = 0; if (this.isConnected) this.#layout(); });
    }
    #layout() {
      const image = this.#image;
      if (!image?.naturalWidth || !this.clientWidth || !this.clientHeight) return;
      const { box, target } = window.matchMedia('(max-width: 749px)').matches
        ? { box: [0, 0, 1, 1], target: 1 }
        : this.#measurement || { box: [0, 0, 1, 1], target: 1 };
      const [l, t, r, b] = box;
      const naturalRatio = image.naturalWidth / image.naturalHeight;
      const declaredRatio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
      // Small responsive derivatives round dimensions; retain the source aspect ratio.
      const ratio = declaredRatio > 0 && Math.abs(declaredRatio - naturalRatio) < .01 ? declaredRatio : naturalRatio;
      const width = this.clientWidth, height = this.clientHeight;
      const photoWidth = Math.min(width * target / (r - l), height * target * ratio / (b - t), Math.min(width, height * ratio) * 1.6);
      const centerX = (l + r) / 2, centerY = (t + b) / 2;
      this.style.setProperty('--cf-photo-width', `${100 * photoWidth / width}%`);
      this.style.setProperty('--cf-ratio', String(ratio));
      this.style.setProperty('--cf-x', `${-100 * centerX}%`);
      this.style.setProperty('--cf-y', `${-100 * centerY}%`);
      this.setAttribute('data-framed', '');
      const slide = this.closest('slideshow-slide');
      if (slide && (slide.hidden || slide.getAttribute('aria-hidden') === 'true')) return;
      const card = this.closest('product-card'), gallery = this.closest('.card-gallery');
      if (!card || !gallery) return;
      const start = Math.max(0, width / 2 - photoWidth * centerX);
      const end = Math.max(0, width / 2 - photoWidth * (1 - centerX));
      card.style.setProperty('--cf-copy-start', `${100 * start / width}%`);
      card.style.setProperty('--cf-copy-end', `${100 * end / width}%`);
      const photoHeight = photoWidth / ratio;
      const top = Math.max(0, height / 2 - photoHeight * centerY);
      const bottom = Math.max(0, height / 2 - photoHeight * (1 - centerY));
      gallery.style.setProperty('--cf-arrow-start', `${100 * start / width}%`);
      gallery.style.setProperty('--cf-arrow-end', `${100 * end / width}%`);
      gallery.style.setProperty('--cf-arrow-top', `${100 * top / height}%`);
      gallery.style.setProperty('--cf-arrow-bottom', `${100 * bottom / height}%`);
      const arrowSize = Math.max(0, Math.min(34, (width - start - end - 14) / 2, height - top - bottom - 14));
      gallery.style.setProperty('--cf-arrow-size', `${arrowSize}px`);
      gallery.setAttribute('data-cf-bounds', '');
    }
  }
  customElements.define('tiffany-catalog-photo', TiffanyCatalogPhoto);
}

